class AvatarSyncManager {
  constructor() {
    this.subscribers = new Map();
    this.currentAvatar = null;
    this.currentUrl = null;
    this.syncEnabled = false;
  }

  // Subscribe to avatar sync events
  subscribe(instanceId, callback) {
    console.log(`AvatarSyncManager: Subscribing instance ${instanceId}`);
    this.subscribers.set(instanceId, callback);
    
    // Send current state immediately to new subscriber if sync is enabled
    if (this.currentAvatar && this.currentUrl && this.syncEnabled) {
      console.log(`AvatarSyncManager: Sending current state to new subscriber ${instanceId}:`, {
        avatar: this.currentAvatar,
        url: this.currentUrl,
        syncEnabled: this.syncEnabled
      });
      // Send current state once after a short delay to allow the subscriber to finish mounting
      setTimeout(() => {
        try {
          if (this.subscribers.has(instanceId) && this.syncEnabled) {
            callback({
              type: 'avatar_changed',
              avatar: this.currentAvatar,
              url: this.currentUrl,
              syncEnabled: this.syncEnabled
            });
          }
        } catch (error) {
          console.error(`Error sending initial state to ${instanceId}:`, error);
        }
      }, 50);
    }
  }

  // Unsubscribe from avatar sync events
  unsubscribe(instanceId) {
    console.log(`AvatarSyncManager: Unsubscribing instance ${instanceId}`);
    this.subscribers.delete(instanceId);
  }

  // Sync avatar change across all instances
  syncAvatarChange(avatarKey, url, sourceInstanceId = null) {
    console.log(`AvatarSyncManager: Syncing avatar change - ${avatarKey} from ${sourceInstanceId}`);
    
    // Update state
    this.currentAvatar = avatarKey;
    this.currentUrl = url;
    this.syncEnabled = true;
    
    // Broadcast to all subscribers once — repeated broadcasts cause race conditions
    // where a second broadcast fires after the scene has already been torn down.
    this.broadcastAvatarChange(sourceInstanceId);
  }

  // Reset all avatars to default
  resetAvatars(sourceInstanceId = null) {
    console.log(`AvatarSyncManager: Resetting avatars from ${sourceInstanceId}`);
    
    // Update state - explicitly disable sync
    this.currentAvatar = null;
    this.currentUrl = null;
    this.syncEnabled = false;
    
    // Broadcast reset to all subscribers with explicit sync disabled flag
    this.broadcastAvatarReset(sourceInstanceId);
  }

  // Broadcast avatar change to all subscribers
  broadcastAvatarChange(sourceInstanceId = null) {
    const event = {
      type: 'avatar_changed',
      avatar: this.currentAvatar,
      url: this.currentUrl,
      syncEnabled: this.syncEnabled,
      timestamp: Date.now()
    };
    
    console.log('AvatarSyncManager: Broadcasting avatar change:', event);
    
    this.subscribers.forEach((callback, instanceId) => {
      if (instanceId !== sourceInstanceId) {
        console.log(`AvatarSyncManager: Notifying subscriber ${instanceId}`);
        try {
          callback(event);
        } catch (error) {
          console.error(`Error notifying subscriber ${instanceId}:`, error);
        }
      }
    });
  }

  // Broadcast avatar reset to all subscribers
  broadcastAvatarReset(sourceInstanceId = null) {
    const event = {
      type: 'avatar_reset',
      syncEnabled: false, // Explicitly set to false when resetting
      avatar: null,
      url: null
    };
    
    console.log('AvatarSyncManager: Broadcasting avatar reset:', event);
    
    this.subscribers.forEach((callback, instanceId) => {
      if (instanceId !== sourceInstanceId) {
        console.log(`AvatarSyncManager: Notifying subscriber ${instanceId} of reset`);
        try {
          callback(event);
        } catch (error) {
          console.error(`Error notifying subscriber ${instanceId}:`, error);
        }
      }
    });
  }

  // Get current sync state
  getCurrentState() {
    return {
      selectedAvatar: this.currentAvatar,
      selectedUrl: this.currentUrl,
      syncEnabled: this.syncEnabled
    };
  }

  // Get sync statistics
  getStats() {
    return {
      subscribers: this.subscribers.size,
      syncEnabled: this.syncEnabled,
      selectedAvatar: this.currentAvatar
    };
  }
}

// Create singleton instance
export const avatarSyncManager = new AvatarSyncManager();
export default avatarSyncManager;
